import {
  resetMaxRteDataReducer,
  GetGenericEntityListingApiType,
  getGenericEntityListingAPI,
  addToaster,
  getOrgIdMaxRte,
  maxRteDiscoveryApi,
  maxRteTransactionApi,
  ErrorInput,
  maxRteDataMappingReducer,
  Coverage,
  getSubResourcesWithoutLayout,
  hideLoaderClearMessage,
  showLoaderwithMessage,
  getClientIntegerationConfig,
  updateMaxRTEShouldAutoScroll,
  MAX_RTE_CONSTANTS,
  showLoader,
  hideLoader,
  getTenantIdFromWindow,
} from '@app-store'
import moment from 'moment'
import { store } from './agent.store'
import {
  ENTITY_NAME,
  HTTP_STATUS_CONSTANTS,
  INTEGRATION_TYPES,
  NAMESPACES,
} from './constants'
import { MAX_RTE_STRINGS } from './strings'
import { v4 as uuid } from 'uuid'
import { PatientAddress, ResponseData } from '../types/maxRTE.types'
export const hasHipaaConsentForProduct = (
  data: ResponseData,
  productName: string
): boolean => {
  if (!data?.c_patient_consents_patient) return false

  const today = new Date()

  return data.c_patient_consents_patient.some((consent) => {
    const extractedProductName =
      RegExp(/^[A-Za-z0-9 ]+/)
        .exec(consent?.product?.trademarked_name ?? '')?.[0]
        ?.trim() || ''
    const productMatch =
      extractedProductName?.toLowerCase() === productName?.toLowerCase()

    const typeMatch =
      consent?.consent_type?.name?.toLowerCase() ===
      MAX_RTE_NAME_SPACE.HIPAA_CONSENT

    const revoked = consent?.revoked

    if (!productMatch || !typeMatch || revoked) return false

    const startDate = consent?.start_date ? new Date(consent.start_date) : null
    const endDate = consent?.expiration_date
      ? new Date(consent.expiration_date)
      : null

    // --- check date conditions ---
    if (startDate && endDate) {
      return today >= startDate && today <= endDate
    }

    if (startDate && !endDate) {
      return today >= startDate
    }

    if (!startDate && endDate) {
      return today <= endDate
    }

    // no dates → accept based only on product & type
    return !revoked
  })
}
export interface Product {
  label: string
  value: string
}

export const getUniqueProductNames = (products: Product[]): Product[] => {
  const cleaned = products
    .map((p) => {
      let name = p.label || ''

      // Keep only the leading alphanumeric + spaces
      // Example: "SIMLANDI® (PEDIATRIC)" → "SIMLANDI"
      //          "SELARSDI™" → "SELARSDI"
      name =
        RegExp(/^[A-Za-z0-9 ]+/)
          .exec(name)?.[0]
          ?.trim() || ''

      return {
        label: name,
        value: p.value,
      }
    })
    .filter(
      (p) => p.label && p.label.toLowerCase() !== 'other' // remove "Other"
    )

  // Deduplicate case-insensitively by label
  const unique = Array.from(
    new Map(cleaned.map((p) => [p.label.toLowerCase(), p])).values()
  )

  return unique
}

const showError = (message: string) =>
  store.dispatch(addToaster({ id: uuid(), message, type: 'error' }))

const validateRequiredFields = (
  values: { first_name: string; last_name: string; date_of_birth: string },
  patientAddress: PatientAddress | undefined,
  orgId: string | undefined
): boolean => {
  return !(
    values?.first_name?.trim() &&
    values?.last_name?.trim() &&
    values?.date_of_birth &&
    patientAddress?.state?.name?.split(' ')?.[0]?.trim() &&
    patientAddress?.zip_code?.trim() &&
    orgId
  )
}

const handleTransaction = async (
  transaction: {
    body: {
      validationErrors?: ErrorInput[]
      coverages: Coverage[]
      message?: string
    }
    statusCode: number
  },
  patientId: number
) => {
  const statusCode = transaction?.statusCode
  const body = transaction?.body
  await fetchInitialData(patientId)
  if (
    statusCode === HTTP_STATUS_CONSTANTS.SUCCESS ||
    body?.validationErrors?.length ||
    body?.coverages?.length
  ) {
    return true
  }

  showError(body?.message ?? ' ')
  return false
}
export const delay = (seconds: number) =>
  new Promise((resolve) => setTimeout(resolve, seconds * 1000))

export const handleMaxRTE = async (
  dataId: number,
  productName: string,
  caseId?: string
) => {
  try {
    store.dispatch(
      showLoaderwithMessage({
        loaderText: NAMESPACES?.MAXRTE_DATA_FETCH_LOADER,
        loaderSubText: NAMESPACES?.MAXRTE_DATA_FETCH_SUB_LOADER,
      })
    )

    store.dispatch(resetMaxRteDataReducer())
    const payload: GetGenericEntityListingApiType = {
      entity_name: ENTITY_NAME.PATIENTS,
      select: [
        ENTITY_NAME.C_PATIENT_ADDRESS,
        ENTITY_NAME.C_PATIENT_CONSENT,
        'first_name',
        'last_name',
        'date_of_birth',
      ],
      filters: [],
      related_entities: {
        c_patient_addresses_c_patients: {
          select: ['zip_code', 'state', 'is_preferred'],
          criteria: [],
        },
        c_patient_consents_patient: {
          select: [
            'consent_type',
            'product',
            'expiration_date',
            'start_date',
            'revoked',
          ],
          criteria: [],
        },
      },
    }

    const patientResponse = await getGenericEntityListingAPI(payload, dataId)
    if (!patientResponse) return false

    // Validation Checks
    // check consent
    // const hasConsent = true
    // const hasConsent = hasHipaaConsentForProduct(patientResponse, productName)
    // if (!hasConsent) {
    //   showError(MAX_RTE_STRINGS.HIPAA_CONSENT_MISSING)
    //   return false
    // }

    const patientAddress =
      patientResponse?.c_patient_addresses_c_patients?.filter(
        (patientAddress: { is_preferred: boolean }) =>
          patientAddress.is_preferred
      )[0]
    const stateCode = patientAddress?.state?.name?.split(' ')?.[0] ?? ''
    if (!stateCode) {
      showError(MAX_RTE_STRINGS.REQUIRED_FIELD_MISSING)
      return false
    }
    const orgIdResponse = await getOrgIdMaxRte(stateCode, productName)
    const orgId = orgIdResponse?.data?.orgId
    const delayTime = orgIdResponse?.data?.delayTime
    const updatedValues = {
      first_name: patientResponse?.first_name,
      last_name: patientResponse?.last_name,
      date_of_birth: patientResponse?.date_of_birth,
    }

    if (validateRequiredFields(updatedValues, patientAddress, orgId)) {
      showError(MAX_RTE_STRINGS.REQUIRED_FIELD_MISSING)
      return false
    }

    const discoveryResp = await maxRteDiscoveryApi(
      {
        patientFirstName: updatedValues.first_name,
        patientLastName: updatedValues.last_name,
        patientBirthDate: updatedValues.date_of_birth,
        organizationId: orgId,
        patientState: stateCode,
        patientZipCode: patientAddress?.zip_code ?? '',
        patientId: dataId,
        productName: (productName ?? '').toUpperCase(),
        ...(caseId ? { caseId } : {}),
      },
      (productName ?? '').toUpperCase()
    )

    if (discoveryResp?.data?.statusCode !== HTTP_STATUS_CONSTANTS.SUCCESS) {
      if (typeof discoveryResp?.data?.body === 'string') {
        showError(discoveryResp?.data?.body)
      } else {
        showError(discoveryResp?.data?.body?.message)
      }
      return false
    }
    await delay(delayTime ?? 20)
    const transaction = await maxRteTransactionApi(
      {
        returnAll: false,
        transactionid: discoveryResp?.data?.body?.raw,
        ...(caseId ? { caseId } : {}),
      },
      (productName ?? '').toUpperCase()
    )

    const transactionStatus = await handleTransaction(transaction?.data, dataId)
    store.dispatch(updateMaxRTEShouldAutoScroll(true))
    if (!transactionStatus) {
      return false
    }
    return true
  } catch (error) {
    store.dispatch(resetMaxRteDataReducer())

    showError(
      (error as { response: { data?: { message?: string } } })?.response?.data
        ?.message ?? MAX_RTE_STRINGS.ELIGIBILITY_ERROR_MSG
    )
    return false
  } finally {
    store.dispatch(hideLoaderClearMessage())
  }
}

export const isEntityEditRoute = (path: string): boolean => {
  const regex = /^\/\d+\/entities\/\d+\/edit\/\d+$/
  return regex.test(path)
}

export const CHECK_ELIGIBILITY_LOCATION = {
  CASE: 'CASE',
  INTAKE: 'INTAKE',
  ENTITIES: 'ENTITIES',
}

export const WORKFLOW_SUB_SERVICE_MAPPING = {
  PAP_NEW_ENROLLMENT: 50,
  PAP_APPEAL: 53,
}

export const ifEligibleToRunMaxRTE = (
  patientId: number | null,
  product: string | null
): boolean => {
  const cleanPatientId = patientId ?? ''
  const cleanProduct = product?.trim() ?? ''

  if (!cleanPatientId && !cleanProduct) {
    showError(MAX_RTE_STRINGS.PATIENT_PRODUCT_REQUIRED)
    return false
  }

  if (!cleanPatientId) {
    showError(MAX_RTE_STRINGS.PATIENT_REQUIRED)
    return false
  }

  if (!cleanProduct) {
    showError(MAX_RTE_STRINGS.PRODUCT_REQUIRED)
    return false
  }

  return true
}

export const extractProductName = (input?: string | null): string => {
  if (!input) return ''

  const match = /^[A-Za-z0-9 ]+/.exec(input)
  return match?.[0]?.trim() ?? ''
}

export const MAX_RTE_NAME_SPACE = {
  ADD_INSURANCE_POLICY_INFORMATION: 'Add Insurance Policy Information',
  ADDED: 'ADDED',
  COPY: 'Copy',
  CHECK_ELIGIBILITY_TITLE: 'Check Eligibility',
  BV_ID_PREFIX: 'BV-',
  PAP_ID_PREFIX: 'PAP-',
  HIPAA_CONSENT: 'hipaa',
}
export interface CriteriaType {
  page?: number
  size?: number
  filterCriteria: {
    direction?: string
    field_name: string
    operation: string
    values?: string[]
  }[]
}
const getPayload = (patientId: number, criteria?: CriteriaType) => ({
  entity_name: ENTITY_NAME.PATIENT_EINSURANCE_DETAILS,
  unassigned: false,
  page: criteria?.page ?? 0,
  size: criteria?.size ?? 25,
  filters: [
    {
      criteria: [
        {
          direction: 'ASC',
          field_name: 'record_status',
          operation: 'LIKE',
          values: [],
        },
        {
          field_name: 'patient_id',
          operation: 'EQUALS',
          values: [`${patientId}`],
        },
        ...(criteria?.filterCriteria ?? [
          {
            direction: 'DESC',
            field_name: 'maxrte_request_date',
          },
        ]),
      ],
    },
  ],
  select: [
    'c_patients_patient_einsurances',
    'maxrte_request_date',
    'error_message',
    'policy_status',
    'policy_rank_id',
    'policy_source_id',
    'payer_name',
    'payer_type',
    'plan_name',
    'policy_id',
    'group_id',
    'pbm_name',
    'pbm_phone',
    'rx_bin',
    'rx_pcn',
    'rx_group',
    'effective_start_date',
    'effective_end_date',
    'external_id',
    'eligibility_end_date',
    'eligibility_start_date',
    'errors',
    'patient_id',
    'benefits_list',
  ],
  // related_entities: {
  //   c_patients_patient_einsurances: {
  //     select: ['id'],
  //     filters: [
  //       {
  //         criteria: [
  //           {
  //             field_name: 'id',
  //             operation: 'EQUALS',
  //             values: [`${patientId}`],
  //           },
  //         ],
  //       },
  //     ],
  //   },
  // },
})
export const fetchInitialData = async (
  patientId: number,
  criteria: CriteriaType = { filterCriteria: [] }
) => {
  try {
    const clientId = getTenantIdFromWindow()
    if (clientId == null) return
    store.dispatch(showLoader())
    const resp = await getClientIntegerationConfig({
      clientId,
      integerationType: INTEGRATION_TYPES.MAXRTE,
    })
    const months = resp.data[0]?.expiration_period
    const fromDate = months
      ? moment().subtract(months, 'months').format('YYYY-MM-DD[T]HH:mm:ss[Z]')
      : undefined
    const currentDate = moment(Date.now()).format('YYYY-MM-DD[T]HH:mm:ss[Z]')
    const baseCriteria = criteria.filterCriteria.some(
      (filter) =>
        filter.field_name === MAX_RTE_CONSTANTS.KEYS.MAXTE_REQUEST_DATE_TIME
    )
      ? criteria.filterCriteria
      : [
          ...criteria.filterCriteria,
          {
            direction: 'DESC',
            field_name: MAX_RTE_CONSTANTS.KEYS.MAXTE_REQUEST_DATE_TIME,
            operation: 'LIKE',
            values: [],
          },
        ]
    criteria.filterCriteria = [
      ...baseCriteria,
      ...(fromDate && currentDate
        ? [
            {
              field_name: 'created_at',
              operation: 'BETWEEN',
              values: [fromDate, currentDate],
            },
          ]
        : []),
    ]
    const payloadData = getPayload(patientId, criteria)
    const res = await getSubResourcesWithoutLayout({
      dataId: `${patientId}`,
      entityName: payloadData.entity_name,
      unassigned: payloadData?.unassigned,
      cols: payloadData?.select,
      filters: payloadData?.filters,
      page: payloadData?.page,
      rowsPerPage: payloadData?.size,
    })
    if (res?.data?.content) {
      store.dispatch(
        maxRteDataMappingReducer({
          response: res?.data?.content,
          resMeta: {
            currentPage: res?.data?.number,
            totalNumberOfElements: res?.data?.totalElements,
            totalPages: res?.data?.totalPages,
            size: res?.data?.size,
          },
        })
      )
    }
  } catch (e) {
    console.log(e)
  } finally {
    store.dispatch(hideLoader())
  }
}
